import { type CountryCode } from "libphonenumber-js";
import {
    getCountryCallingCode,
    isPossiblePhoneNumber,
    isSupportedCountry,
    parsePhoneNumber,
} from "react-phone-number-input";

import { type Countries } from "types/countries";

import yup from "utilities/form/yup";
import { isNullOrWhitespace } from "utilities/string/string";

import { type ICellPhoneInputFormValues } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";

export const cellPhoneValidator = yup
    .string()
    .nullable()
    .label("Cell Phone")
    .test("validCountryCode", "Country must be selected", (value) => {
        if (isNullOrWhitespace(value)) {
            return true;
        }
        return value.startsWith("+");
    })
    .test("validPhoneNumber", "Invalid cell phone number", (value) => {
        if (isNullOrWhitespace(value)) {
            return true;
        }
        return isPossiblePhoneNumber(value);
    })
    .max(25);

export const phoneValidator = yup
    .string()
    .nullable()
    .label("Phone")
    .test("validCountryCode", "Country must be selected", (value: string | null | undefined) => {
        if (isNullOrWhitespace(value)) {
            return true;
        }
        return value!.startsWith("+");
    })
    .test("validPhoneNumber", "Invalid phone number", (value: string | null | undefined) => {
        if (isNullOrWhitespace(value)) {
            return true;
        }
        return isPossiblePhoneNumber(value!);
    })
    .max(25);

export const cellPhoneFormValueValidator = yup.object<ICellPhoneInputFormValues>({
    phoneCell: cellPhoneValidator,
    smsOptInStatus: yup.number(),
    verificationRequest: yup.number(),
});

export const getCellPhoneFieldValue = (
    cellPhoneNumber: string,
    countryCode: Countries,
    canEdit: boolean,
) => {
    if (!canEdit) {
        return cellPhoneNumber;
    }
    if (isNullOrWhitespace(cellPhoneNumber)) {
        return "";
    }
    if (isPossiblePhoneNumber(cellPhoneNumber)) {
        return cellPhoneNumber;
    }

    if (cellPhoneNumber.startsWith("+")) {
        return cellPhoneNumber;
    }

    if (!parsePhoneNumber(cellPhoneNumber)?.country) {
        const isSupported = !!countryCode && isSupportedCountry(countryCode.toString());
        if (isSupported) {
            const callingCode = getCountryCallingCode(countryCode as CountryCode);
            if (cellPhoneNumber.startsWith(callingCode)) {
                return "+" + cellPhoneNumber;
            }
            return "+" + callingCode + cellPhoneNumber;
        }
    }
    return cellPhoneNumber;
};
